import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { CATEGORIES } from "../lib/categories";
import ProductCard from "../components/ProductCard";
import { ArrowRight, ShieldCheck, Truck, Award } from "lucide-react";

export default function Home() {
  const { data: featured = [] } = useQuery({
    queryKey: ["products", "featured"],
    queryFn: async () => (await api.get("/products", { params: { featured: true, limit: 12 } })).data,
  });

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="border-b border-[#0a0a0a] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <div className="rh-overline mb-6">FEB 2026 · ISSUE Nº 01 · AUTHORIZED RETAILER</div>
              <h1 className="font-display font-black tracking-tighter leading-[0.92] text-5xl sm:text-6xl lg:text-7xl">
                Nine categories.<br />
                One <span className="text-[#002fa7]">authorized</span><br />
                destination.
              </h1>
              <p className="mt-8 text-[#4a4a4a] text-base lg:text-lg max-w-xl leading-relaxed">
                From flagship headphones to roof-rack systems, every item ships from an authorized
                distributor — full warranty, fast shipping, no grey-market guesswork.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/shop" data-testid="hero-shop-btn" className="rh-btn-primary inline-flex items-center gap-2">
                  Browse all products <ArrowRight size={16} />
                </Link>
                <Link to="/shop/consumer-electronics" className="rh-btn-secondary">
                  Featured: Electronics
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] overflow-hidden border border-[#0a0a0a]">
                <img src="https://images.unsplash.com/photo-1756705533779-105bf34e0722?w=1200&q=85"
                     alt="Premium retail showcase" className="w-full h-full object-cover" />
                <div className="absolute top-4 left-4 bg-white px-3 py-2 border border-[#0a0a0a]">
                  <span className="rh-overline">SHIP · WORLDWIDE</span>
                </div>
                <div className="absolute bottom-4 right-4 bg-[#002fa7] text-white px-3 py-2">
                  <span className="font-mono text-[10px] tracking-widest uppercase">+1,200 SKUs</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-b border-[#0a0a0a] bg-[#f5f5f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, t: "Authorized Dealer", d: "Direct from manufacturers" },
            { icon: Award, t: "Full Warranty", d: "Manufacturer-backed coverage" },
            { icon: Truck, t: "Free Shipping $75+", d: "Ships within 24h" },
            { icon: ArrowRight, t: "30-Day Returns", d: "Hassle-free policy" },
          ].map((b) => (
            <div key={b.t} className="flex items-start gap-3">
              <b.icon size={22} className="text-[#002fa7] mt-1 flex-shrink-0" />
              <div>
                <div className="font-semibold text-sm">{b.t}</div>
                <div className="text-xs text-[#4a4a4a]">{b.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BENTO CATEGORY GRID */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="rh-overline mb-3">Nº 02 — DEPARTMENTS</div>
              <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">Browse by category</h2>
            </div>
            <Link to="/shop" className="hidden md:inline-block text-sm font-semibold uppercase tracking-widest underline" data-testid="view-all-cats">View all →</Link>
          </div>

          <div className="grid grid-cols-12 gap-3 lg:gap-4">
            {CATEGORIES.map((c, idx) => {
              const spans = [
                "col-span-12 md:col-span-7 row-span-2",
                "col-span-6 md:col-span-5",
                "col-span-6 md:col-span-5",
                "col-span-12 md:col-span-4",
                "col-span-6 md:col-span-4",
                "col-span-6 md:col-span-4",
                "col-span-6 md:col-span-4",
                "col-span-6 md:col-span-4",
                "col-span-12 md:col-span-4",
              ];
              const heights = [
                "h-[420px] md:h-[600px]",
                "h-[280px]",
                "h-[280px]",
                "h-[240px]",
                "h-[240px]",
                "h-[240px]",
                "h-[240px]",
                "h-[240px]",
                "h-[240px]",
              ];
              return (
                <Link
                  key={c.slug}
                  to={`/shop/${c.slug}`}
                  data-testid={`category-tile-${c.slug}`}
                  className={`group relative overflow-hidden border border-[#0a0a0a] ${spans[idx]} ${heights[idx]}`}
                >
                  <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover rh-hover-img" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute top-4 left-4 font-mono text-[11px] tracking-[0.25em] uppercase text-white/90">
                    Nº {c.code}
                  </div>
                  <div className="absolute bottom-5 left-5 right-5 text-white">
                    <h3 className="font-display font-black text-2xl lg:text-3xl tracking-tight leading-none">{c.name}</h3>
                    <p className="mt-2 text-xs lg:text-sm text-white/80">{c.tagline}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="bg-[#f5f5f5] border-y border-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="rh-overline mb-3">Nº 03 — EDITORS PICKS</div>
              <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">Featured this week</h2>
            </div>
            <Link to="/shop" className="hidden md:inline-block text-sm font-semibold uppercase tracking-widest underline">All products →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {featured.slice(0, 8).map((p) => <ProductCard key={p.product_id} p={p} testIdPrefix="featured" />)}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="bg-[#0a0a0a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="rh-overline text-white/60 mb-4">Nº 04 — MANIFESTO</div>
            <h3 className="font-display font-black text-3xl lg:text-4xl tracking-tighter leading-tight">Why authorized matters.</h3>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-8">
            {[
              { n: "01", t: "Sourced direct", d: "We buy from the manufacturer or their authorized distributors — never grey market." },
              { n: "02", t: "Warranty intact", d: "Every product retains its full manufacturer warranty. Documented and honored." },
              { n: "03", t: "Real support", d: "Our specialists know the gear — not call-center scripts. Reach them anytime." },
            ].map((b) => (
              <div key={b.n} className="border-t border-white/20 pt-5">
                <div className="font-mono text-xs tracking-widest text-[#ff3b30] mb-2">{b.n}</div>
                <div className="font-semibold mb-2">{b.t}</div>
                <p className="text-sm text-white/70 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
